import { useState, useEffect, useCallback } from 'react'
import Modal from '../../components/Modal'
import Boton from '../../components/Boton'
import CampoFecha from '../../components/CampoFecha'
import MoneyInput from '../../components/MoneyInput'
import { erpApi } from '../../api/erp'
import { fmtFecha } from '../../utils/fecha'
import ModalAltaTercero from './ModalAltaTercero'
import { nuevoUuid, mensajeError, fmtImporte, TIPO_LABEL, tonoEstado, useDepositos, renglonesValidos, renglonesPayload, hoverFila, st } from './_helpers'
import { SelectDeposito, BuscadorTercero, RenglonesEditor, AvisoErp, CajaError, CajaOk, Campo, Badge } from './_comun'
import { colors, text, space } from '../../design/tokens'

const fecha = (s) => (s && String(s).includes('/')) ? s : fmtFecha(s)

/**
 * EMP 900 · Ventas — un listado por tipo de comprobante del ERP (como el menú
 * de LGR: Facturas · Recibos · Remitos), con la acción de carga que le toca:
 *   facturas → circuito A (Martin, 25 ago): ventas factura (lista LISTALM),
 *              NC, devolución. El remito NO se hace acá: lo hace DEPÓSITO
 *              contra la factura, en partes, hasta cumplirla.
 *   recibos  → cobro con imputación a facturas pendientes (o a cuenta).
 *   remitos  → solo consulta.
 * Cada formulario nace con su uuid y lo conserva hasta cerrarse: reintentar
 * = mismo comprobante.
 */
const VISTAS = {
  factura: { titulo: 'Facturas', tipos: ['factura', 'nota_credito'], sub: 'Circuito factura (lista LISTALM), valores finales. Incluye notas de crédito y devoluciones. El remito lo hace Depósito contra la factura.' },
  recibo: { titulo: 'Recibos', tipos: ['recibo'], sub: 'Cobros de la 900. Sin facturas elegidas, el ERP imputa de la más vieja a la más nueva; se puede cobrar a cuenta.' },
  remito: { titulo: 'Remitos', tipos: ['remito'], sub: 'Los remitos los hace Depósito contra la factura: varios remitos por factura y un remito puede cubrir varias facturas.' },
}

export default function Emp900ComprobantesPage({ tipo }) {
  const vista = VISTAS[tipo]
  const [comprobantes, setComprobantes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tercero, setTercero] = useState(null)
  const [modal, setModal] = useState(null) // 'factura' | 'recibo' | 'nc' | {detalle:id}

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      // La API filtra por UN tipo; Facturas muestra factura + NC, así que
      // se trae sin tipo y se filtra acá (últimos 100 del ERP).
      const unTipo = vista.tipos.length === 1 ? vista.tipos[0] : undefined
      const { data } = await erpApi.comprobantes({ tipo: unTipo, tercero_id: tercero?.id })
      setComprobantes((data.comprobantes || []).filter(c => vista.tipos.includes(c.tipo)))
      setError('')
    } catch (e) { setError(mensajeError(e)) } finally { setLoading(false) }
  }, [vista, tercero])

  useEffect(() => { cargar() }, [cargar])

  const cerrarYRecargar = () => { setModal(null); cargar() }

  return (
    <div>
      <div style={st.header}>
        <h2 style={st.title}>{vista.titulo}</h2>
        <div style={st.acciones}>
          {tipo === 'factura' && <Boton variante="secondary" onClick={() => setModal('nc')}>Nota de crédito / Devolución</Boton>}
          {tipo === 'factura' && <Boton variante="primary" onClick={() => setModal('factura')}>+ Nueva factura</Boton>}
          {tipo === 'recibo' && <Boton variante="primary" onClick={() => setModal('recibo')}>+ Nuevo recibo</Boton>}
        </div>
      </div>
      <p style={st.sub}>{vista.sub} Los anulados se siguen viendo con su estado.</p>

      <div style={st.filtros}>
        <div style={{ flex: 1, minWidth: 280 }}><BuscadorTercero value={tercero} onChange={setTercero} rol="cliente" placeholder="Filtrar por cliente…" /></div>
        <Boton variante={tercero ? 'secondary' : 'ghost'} onClick={() => setTercero(null)}>Limpiar</Boton>
      </div>

      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead>
            <tr>{['Número', 'Fecha', 'Tipo', 'Cliente', 'Total', 'Estado', ''].map((h, i) => <th key={i} style={{ ...st.th, ...(h === 'Total' ? { textAlign: 'right' } : {}) }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading && comprobantes.length === 0 ? <tr><td colSpan={7} style={st.empty}>Cargando…</td></tr>
              : error ? <tr><td colSpan={7} style={st.empty}>{error}</td></tr>
                : comprobantes.length === 0 ? <tr><td colSpan={7} style={st.empty}>Todavía no hay comprobantes de la 900 en el ERP.</td></tr>
                  : comprobantes.map(c => (
                    <tr key={c.id} style={st.trClick} {...hoverFila} title="Ver detalle" onClick={() => setModal({ detalle: c.id })}>
                      <td style={st.tdMono}>{c.numero}</td>
                      <td style={st.tdFecha}>{fecha(c.fecha)}</td>
                      <td style={st.td}><Badge tono="neutro">{TIPO_LABEL[c.tipo] || c.tipo}</Badge></td>
                      <td style={st.td}>{c.cliente}</td>
                      <td style={st.tdImporte}>{fmtImporte(c.total)}</td>
                      <td style={st.td}><Badge tono={tonoEstado(c.estado)}>{c.estado}</Badge></td>
                      <td style={st.tdSec}>{String(c.observaciones || '').includes('[AVISO]') && <span title={c.observaciones}>⚠</span>}</td>
                    </tr>
                  ))}
          </tbody>
        </table>
      </div>

      {modal === 'factura' && <FacturaModal onClose={cerrarYRecargar} />}
      {modal === 'recibo' && <ReciboModal onClose={cerrarYRecargar} />}
      {modal === 'nc' && <NcModal onClose={cerrarYRecargar} />}
      {modal?.detalle && <DetalleModal id={modal.detalle} onClose={cerrarYRecargar} />}
    </div>
  )
}

// ---------------------------------------------------------------- Factura (circuito A)

function FacturaModal({ onClose }) {
  const [uuid, setUuid] = useState(nuevoUuid)
  const [tercero, setTercero] = useState(null)
  const [fechaDoc, setFechaDoc] = useState('')
  const [vence, setVence] = useState('')
  const [obs, setObs] = useState('')
  const [renglones, setRenglones] = useState([])
  const [alta, setAlta] = useState(false)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)

  const listo = tercero && renglonesValidos(renglones)

  const enviar = async () => {
    setError(''); setEnviando(true)
    try {
      const { data } = await erpApi.facturar({
        uuid, tercero_id: tercero.id, renglones: renglonesPayload(renglones),
        fecha: fechaDoc || undefined, fecha_vencimiento: vence || undefined, observaciones: obs || undefined,
      })
      setResultado(data)
    } catch (e) { setError(mensajeError(e)) } finally { setEnviando(false) }
  }

  const otra = () => { setUuid(nuevoUuid()); setRenglones([]); setObs(''); setResultado(null); setError('') }

  return (
    <Modal title="Nueva factura" onClose={onClose} maxWidth="860px">
      {alta && <ModalAltaTercero rol="cliente" onClose={() => setAlta(false)} onCreado={t => { setTercero(t); setAlta(false) }} />}
      {resultado ? (
        <div style={st.seccion}>
          <CajaOk titulo={`Factura ${resultado.comprobante?.numero}${resultado.repetida ? ' (ya estaba registrada)' : ''}`}>
            Total {fmtImporte(resultado.comprobante?.total)} · Saldo del cliente {fmtImporte(resultado.saldo_cliente)}. La mercadería sale cuando Depósito la remita.
            <AvisoErp observaciones={resultado.comprobante?.observaciones} />
          </CajaOk>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cerrar</Boton>
            <Boton variante="secondary" onClick={otra}>Otra factura</Boton>
          </div>
        </div>
      ) : (
        <div style={st.seccion}>
          <p style={{ ...st.info, margin: 0 }}>
            La deuda nace al facturar; el stock sale cuando Depósito remite contra esta factura (en partes, hasta cumplirla). Precio sugerido de LISTALM, editable.
          </p>
          <Campo label="Cliente"><BuscadorTercero value={tercero} onChange={setTercero} rol="cliente" onNuevo={() => setAlta(true)} /></Campo>
          {tercero?.bloqueado && <div style={st.aviso}>⚠ Cliente bloqueado{tercero.motivo_bloqueo ? `: ${tercero.motivo_bloqueo}` : ''}. La factura se registra igual; el ERP lo deja anotado como aviso.</div>}
          <div style={st.grid3}>
            <Campo label="Fecha (vacío = hoy)"><CampoFecha style={st.input} value={fechaDoc} onChange={setFechaDoc} /></Campo>
            <Campo label="Vencimiento"><CampoFecha style={st.input} value={vence} onChange={setVence} /></Campo>
          </div>
          <Campo label="Renglones"><RenglonesEditor renglones={renglones} onChange={setRenglones} sugerirPrecio /></Campo>
          <Campo label="Observaciones"><textarea style={st.textarea} value={obs} onChange={e => setObs(e.target.value)} maxLength={250} /></Campo>
          <CajaError>{error}</CajaError>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
            <Boton variante="primary" onClick={enviar} disabled={!listo || enviando}>{enviando ? 'Enviando al ERP…' : 'Facturar'}</Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------- Recibo

function ReciboModal({ onClose }) {
  const [uuid, setUuid] = useState(nuevoUuid)
  const [tercero, setTercero] = useState(null)
  const [importe, setImporte] = useState('')
  const [fechaDoc, setFechaDoc] = useState('')
  const [obs, setObs] = useState('')
  const [cuenta, setCuenta] = useState(null)
  const [ids, setIds] = useState(new Set())
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)

  const elegirTercero = (t) => { setTercero(t); setCuenta(null); setIds(new Set()); setError('') }
  useEffect(() => {
    if (!tercero) return
    erpApi.cuenta(tercero.id, 'cliente').then(({ data }) => setCuenta(data)).catch(e => setError(mensajeError(e)))
  }, [tercero])

  const toggle = (id) => setIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  const enviar = async () => {
    setError(''); setEnviando(true)
    try {
      const { data } = await erpApi.cobrar({
        uuid, tercero_id: tercero.id, importe,
        factura_ids: ids.size ? [...ids].map(i => cuenta.pendientes.find(p => p.id === i)?.origen_id).filter(Boolean) : undefined,
        fecha: fechaDoc || undefined, observaciones: obs || undefined,
      })
      setResultado(data)
    } catch (e) { setError(mensajeError(e)) } finally { setEnviando(false) }
  }

  const otro = () => { setUuid(nuevoUuid()); setImporte(''); setObs(''); setResultado(null); elegirTercero(null) }

  return (
    <Modal title="Recibo" onClose={onClose} maxWidth="680px">
      {resultado ? (
        <div style={st.seccion}>
          <CajaOk titulo={`Recibo ${resultado.comprobante?.numero}${resultado.repetida ? ' (ya estaba registrado)' : ''}`}>
            Cobrado {fmtImporte(resultado.comprobante?.total)} · Saldo del cliente {fmtImporte(resultado.saldo_cliente)}
          </CajaOk>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cerrar</Boton>
            <Boton variante="secondary" onClick={otro}>Otro recibo</Boton>
          </div>
        </div>
      ) : (
        <div style={st.seccion}>
          <Campo label="Cliente"><BuscadorTercero value={tercero} onChange={elegirTercero} rol="cliente" /></Campo>
          {cuenta && (
            <div>
              <div style={st.label}>Saldo actual: <span style={st.numero}>{fmtImporte(cuenta.saldo)}</span></div>
              {cuenta.pendientes?.length > 0 ? (
                <table style={{ ...st.table, marginTop: space.sm }}>
                  <thead><tr><th style={st.th}></th><th style={st.th}>Documento</th><th style={st.th}>Fecha</th><th style={{ ...st.th, textAlign: 'right' }}>Importe</th><th style={{ ...st.th, textAlign: 'right' }}>Pendiente</th></tr></thead>
                  <tbody>
                    {cuenta.pendientes.map(p => (
                      <tr key={p.id} style={st.trClick} {...hoverFila} onClick={() => toggle(p.id)}>
                        <td style={st.td}><input type="checkbox" checked={ids.has(p.id)} onChange={() => toggle(p.id)} onClick={e => e.stopPropagation()} /></td>
                        <td style={st.tdMono}>{p.documento}</td>
                        <td style={st.tdFecha}>{fecha(p.fecha)}</td>
                        <td style={st.tdImporte}>{fmtImporte(p.importe)}</td>
                        <td style={st.tdImporte}>{fmtImporte(p.pendiente)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p style={{ ...st.info, marginTop: space.sm }}>Sin facturas pendientes: el cobro queda a cuenta (saldo a favor).</p>}
              <p style={{ ...st.info, marginTop: space.sm }}>Sin elegir facturas, el ERP imputa de la más vieja a la más nueva.</p>
            </div>
          )}
          <div style={st.grid2}>
            <Campo label="Importe"><MoneyInput style={st.input} value={importe} onChange={setImporte} placeholder="" autoFocus /></Campo>
            <Campo label="Fecha (vacío = hoy)"><CampoFecha style={st.input} value={fechaDoc} onChange={setFechaDoc} /></Campo>
          </div>
          <Campo label="Observaciones"><input style={st.input} value={obs} onChange={e => setObs(e.target.value)} maxLength={250} /></Campo>
          <CajaError>{error}</CajaError>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
            <Boton variante="primary" onClick={enviar} disabled={!tercero || !(parseFloat(importe) > 0) || enviando}>{enviando ? 'Enviando al ERP…' : 'Registrar cobro'}</Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------- NC / Devolución

function NcModal({ onClose }) {
  const depositos = useDepositos()
  const [uuid, setUuid] = useState(nuevoUuid)
  const [tipo, setTipo] = useState('nc')
  const [tercero, setTercero] = useState(null)
  const [facturas, setFacturas] = useState([])
  const [facturaId, setFacturaId] = useState('')
  const [deposito, setDeposito] = useState('')
  const [fechaDoc, setFechaDoc] = useState('')
  const [obs, setObs] = useState('')
  const [renglones, setRenglones] = useState([])
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)

  const elegirTercero = (t) => { setTercero(t); setFacturas([]); setFacturaId('') }
  useEffect(() => {
    if (!tercero) return
    erpApi.comprobantes({ tipo: 'factura', tercero_id: tercero.id })
      .then(({ data }) => setFacturas((data.comprobantes || []).filter(f => f.estado !== 'anulado')))
      .catch(() => {})
  }, [tercero])

  const listo = tercero && renglonesValidos(renglones) && (tipo === 'nc' || deposito)

  const enviar = async () => {
    setError(''); setEnviando(true)
    const base = { uuid, tercero_id: tercero.id, renglones: renglonesPayload(renglones), factura_id: facturaId || undefined, fecha: fechaDoc || undefined, observaciones: obs || undefined }
    try {
      const { data } = tipo === 'nc' ? await erpApi.notaCredito(base) : await erpApi.devolucion({ ...base, deposito })
      setResultado(data)
    } catch (e) { setError(mensajeError(e)) } finally { setEnviando(false) }
  }

  const otra = () => { setUuid(nuevoUuid()); setRenglones([]); setObs(''); setResultado(null) }

  return (
    <Modal title="Nota de crédito / Devolución" onClose={onClose} maxWidth="860px">
      {resultado ? (
        <div style={st.seccion}>
          <CajaOk titulo={`Nota de crédito ${resultado.comprobante?.numero}${resultado.repetida ? ' (ya estaba registrada)' : ''}`}>
            {fmtImporte(resultado.comprobante?.total)}
            {resultado.saldo_cliente != null && <> · Saldo del cliente {fmtImporte(resultado.saldo_cliente)}</>}
            {tipo === 'devolucion' && <> · La mercadería reingresó al depósito {deposito}.</>}
          </CajaOk>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cerrar</Boton>
            <Boton variante="secondary" onClick={otra}>Otra</Boton>
          </div>
        </div>
      ) : (
        <div style={st.seccion}>
          <div style={{ display: 'flex', gap: space.xl }}>
            <label style={st.check}><input type="radio" checked={tipo === 'nc'} onChange={() => setTipo('nc')} /> Nota de crédito (solo cuenta corriente)</label>
            <label style={st.check}><input type="radio" checked={tipo === 'devolucion'} onChange={() => setTipo('devolucion')} /> Devolución (NC + reingreso de stock)</label>
          </div>
          <Campo label="Cliente"><BuscadorTercero value={tercero} onChange={elegirTercero} rol="cliente" /></Campo>
          <div style={st.grid3}>
            <Campo label="Imputar a factura (opcional)">
              <select style={st.input} value={facturaId} onChange={e => setFacturaId(e.target.value)} disabled={!tercero}>
                <option value="">Sin imputar</option>
                {facturas.map(f => <option key={f.id} value={f.id}>{f.numero} · {fecha(f.fecha)} · {fmtImporte(f.total)}</option>)}
              </select>
            </Campo>
            {tipo === 'devolucion' && <Campo label="Depósito de reingreso"><SelectDeposito value={deposito} onChange={setDeposito} depositos={depositos} /></Campo>}
            <Campo label="Fecha (vacío = hoy)"><CampoFecha style={st.input} value={fechaDoc} onChange={setFechaDoc} /></Campo>
          </div>
          <Campo label="Renglones"><RenglonesEditor renglones={renglones} onChange={setRenglones} sugerirPrecio /></Campo>
          <Campo label="Observaciones"><textarea style={st.textarea} value={obs} onChange={e => setObs(e.target.value)} maxLength={250} /></Campo>
          <CajaError>{error}</CajaError>
          <div style={st.pie}>
            <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
            <Boton variante="primary" onClick={enviar} disabled={!listo || enviando}>{enviando ? 'Enviando al ERP…' : (tipo === 'nc' ? 'Emitir nota de crédito' : 'Registrar devolución')}</Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------- Detalle + anular

function DetalleModal({ id, onClose }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [anulando, setAnulando] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [uuid] = useState(nuevoUuid)
  const [enviando, setEnviando] = useState(false)

  const cargar = useCallback(() => {
    erpApi.comprobante(id).then(({ data }) => setData(data)).catch(e => setError(mensajeError(e)))
  }, [id])
  useEffect(() => { cargar() }, [cargar])

  const anular = async () => {
    setError(''); setEnviando(true)
    try {
      await erpApi.anularVenta(id, { uuid, motivo })
      setAnulando(false); cargar()
    } catch (e) { setError(mensajeError(e)) } finally { setEnviando(false) }
  }

  const c = data?.comprobante
  return (
    <Modal title={c ? `${TIPO_LABEL[c.tipo] || c.tipo} ${c.numero}` : 'Comprobante'} onClose={onClose} maxWidth="760px">
      {!c ? <p style={st.info}>{error || 'Cargando…'}</p> : (
        <div style={st.seccion}>
          <div style={st.grid3}>
            <div><div style={st.label}>Fecha</div><div style={st.numero}>{fecha(c.fecha)}</div></div>
            <div><div style={st.label}>Cliente</div><div>{c.cliente}</div></div>
            <div><div style={st.label}>Estado</div><Badge tono={tonoEstado(c.estado)}>{c.estado}</Badge></div>
          </div>
          <AvisoErp observaciones={c.observaciones} />
          {c.observaciones && !String(c.observaciones).includes('[AVISO]') && <div style={st.info}>{c.observaciones}</div>}
          <table style={st.table}>
            <thead><tr><th style={st.th}>SKU</th><th style={st.th}>Descripción</th><th style={{ ...st.th, textAlign: 'right' }}>Cant.</th><th style={{ ...st.th, textAlign: 'right' }}>Precio</th><th style={{ ...st.th, textAlign: 'right' }}>Total</th></tr></thead>
            <tbody>
              {(data.items || []).map((i, k) => (
                <tr key={k} style={st.tr}>
                  <td style={st.tdMono}>{i.sku}</td>
                  <td style={st.td}>{i.descripcion}</td>
                  <td style={st.tdImporte}>{Number(i.cantidad)}</td>
                  <td style={st.tdImporte}>{fmtImporte(i.precio_unitario)}</td>
                  <td style={st.tdImporte}>{fmtImporte(i.total)}</td>
                </tr>
              ))}
              {(data.items || []).length === 0 && <tr><td colSpan={5} style={st.empty}>Sin renglones (recibo).</td></tr>}
            </tbody>
            <tfoot><tr><td colSpan={4} style={{ ...st.td, textAlign: 'right', color: colors.textSec }}>Total</td><td style={{ ...st.tdImporte, fontSize: text.title }}>{fmtImporte(c.total)}</td></tr></tfoot>
          </table>
          {anulando ? (
            <div>
              <Campo label="Motivo de la anulación (obligatorio)"><input style={st.input} value={motivo} onChange={e => setMotivo(e.target.value)} autoFocus maxLength={250} /></Campo>
              <p style={{ ...st.info, marginTop: space.sm }}>Nada se borra: el comprobante queda anulado y se revierte lo que movió (cuenta corriente o stock). Un cobro parcial sobre una factura anulada queda como saldo a favor.</p>
              <CajaError>{error}</CajaError>
              <div style={st.pie}>
                <Boton variante="ghost" onClick={() => setAnulando(false)}>Cancelar</Boton>
                <Boton variante="destructive" onClick={anular} disabled={motivo.trim().length < 3 || enviando}>{enviando ? 'Anulando…' : 'Confirmar anulación'}</Boton>
              </div>
            </div>
          ) : (
            <div style={st.pie}>
              <CajaError>{error}</CajaError>
              <Boton variante="ghost" onClick={onClose}>Cerrar</Boton>
              {/* Imprimir recién cuando el ERP devuelva el tercero con domicilio
                  (docs/lgr-pedidos-al-erp.md): un papel sin la dirección no se imprime. */}
              {data.tercero?.domicilio && c.tipo !== 'remito' && (
                <Boton variante="secondary" onClick={() => window.open(`/emp900/imprimir/${c.id}`, '_blank', 'noopener')}>Imprimir</Boton>
              )}
              {c.estado !== 'anulado' && <Boton variante="destructive" onClick={() => setAnulando(true)}>Anular</Boton>}
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
